import { motion } from 'framer-motion'
import { Mail, Phone, MapPin, Send } from 'lucide-react'
import { GlassmorphicCard } from '@/components/ui/glassmorphic-card'

export default function ContactPage() {
    return (
        <div className="min-h-screen">
            {/* Hero Section */}
            <section className="relative pt-32 pb-16 overflow-hidden">


                <div className="container mx-auto px-4 relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="text-center max-w-4xl mx-auto"
                    >
                        <span className="inline-block px-4 py-2 rounded-full glass-dark text-sm font-medium text-primary-blue border border-primary-blue/30 mb-6">
                            Get in Touch
                        </span>
                        <h1 className="text-5xl md:text-6xl lg:text-7xl font-display font-bold mb-6">
                            <span className="text-theme-primary">Let's Start Your</span>
                            <span className="block mt-2 gradient-text">NEET Journey</span>
                        </h1>
                        <p className="text-xl text-theme-secondary">
                            Have questions? We're here to help you succeed
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* Contact Form & Info */}
            <section className="py-24">
                <div className="container mx-auto px-4">
                    <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
                        {/* Contact Form */}
                        <motion.div
                            initial={{ opacity: 0, x: -20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6 }}
                        >
                            <GlassmorphicCard variant="elevated" className="p-8">
                                <h2 className="text-3xl font-display font-bold text-theme-primary mb-6">
                                    Send us a message
                                </h2>
                                <form className="space-y-6">
                                    <div>
                                        <label htmlFor="name" className="block text-sm font-medium text-theme-secondary mb-2">
                                            Full Name
                                        </label>
                                        <input
                                            type="text"
                                            id="name"
                                            className="w-full px-4 py-3 bg-white/80 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-700/50 rounded-lg text-theme-primary placeholder-slate-400 focus:outline-none focus:border-primary-blue transition-colors"
                                            placeholder="Enter your name"
                                        />
                                    </div>

                                    <div>
                                        <label htmlFor="email" className="block text-sm font-medium text-theme-secondary mb-2">
                                            Email Address
                                        </label>
                                        <input
                                            type="email"
                                            id="email"
                                            className="w-full px-4 py-3 bg-white/80 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-700/50 rounded-lg text-theme-primary placeholder-slate-400 focus:outline-none focus:border-primary-blue transition-colors"
                                            placeholder="your.email@example.com"
                                        />
                                    </div>

                                    <div>
                                        <label htmlFor="phone" className="block text-sm font-medium text-theme-secondary mb-2">
                                            Phone Number
                                        </label>
                                        <input
                                            type="tel"
                                            id="phone"
                                            className="w-full px-4 py-3 bg-white/80 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-700/50 rounded-lg text-theme-primary placeholder-slate-400 focus:outline-none focus:border-primary-blue transition-colors"
                                            placeholder="+91 98765 43210"
                                        />
                                    </div>

                                    <div>
                                        <label htmlFor="subject" className="block text-sm font-medium text-theme-secondary mb-2">
                                            Subject
                                        </label>
                                        <input
                                            type="text"
                                            id="subject"
                                            className="w-full px-4 py-3 bg-white/80 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-700/50 rounded-lg text-theme-primary placeholder-slate-400 focus:outline-none focus:border-primary-blue transition-colors"
                                            placeholder="How can we help?"
                                        />
                                    </div>

                                    <div>
                                        <label htmlFor="message" className="block text-sm font-medium text-theme-secondary mb-2">
                                            Message
                                        </label>
                                        <textarea
                                            id="message"
                                            rows={5}
                                            className="w-full px-4 py-3 bg-white/80 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-700/50 rounded-lg text-theme-primary placeholder-slate-400 focus:outline-none focus:border-primary-blue transition-colors resize-none"
                                            placeholder="Tell us more about your query..."
                                        />
                                    </div>

                                    <button
                                        type="submit"
                                        className="w-full px-8 py-4 bg-primary-blue hover:bg-blue-600 text-white font-semibold rounded-lg glow-primary transition-all duration-300 flex items-center justify-center gap-2"
                                    >
                                        Send Message
                                        <Send className="h-5 w-5" />
                                    </button>
                                </form>
                            </GlassmorphicCard>
                        </motion.div>

                        {/* Contact Info */}
                        <motion.div
                            initial={{ opacity: 0, x: 20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6 }}
                            className="space-y-6"
                        >
                            <div>
                                <h2 className="text-3xl font-display font-bold text-theme-primary mb-6">
                                    Contact Information
                                </h2>
                                <p className="text-theme-secondary leading-relaxed mb-8">
                                    Reach out to us through any of these channels. Our team is available
                                    Monday to Saturday, 9 AM to 8 PM IST.
                                </p>
                            </div>

                            <GlassmorphicCard className="p-6 hover:scale-105 transition-all duration-300">
                                <div className="flex items-start gap-4">
                                    <div className="p-3 rounded-xl bg-primary-blue/20 text-primary-blue">
                                        <Mail className="h-6 w-6" />
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-theme-primary mb-1">Email</h3>
                                        <p className="text-theme-muted">support@futurefounder.in</p>
                                        <p className="text-theme-muted">admissions@futurefounder.in</p>
                                    </div>
                                </div>
                            </GlassmorphicCard>

                            <GlassmorphicCard className="p-6 hover:scale-105 transition-all duration-300">
                                <div className="flex items-start gap-4">
                                    <div className="p-3 rounded-xl bg-accent-gold/20 text-accent-gold">
                                        <Phone className="h-6 w-6" />
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-theme-primary mb-1">Phone</h3>
                                        <p className="text-theme-muted">+91 98765 43210</p>
                                        <p className="text-theme-muted">+91 98765 43211 (WhatsApp)</p>
                                    </div>
                                </div>
                            </GlassmorphicCard>

                            <GlassmorphicCard className="p-6 hover:scale-105 transition-all duration-300">
                                <div className="flex items-start gap-4">
                                    <div className="p-3 rounded-xl bg-green-500/20 text-green-500">
                                        <MapPin className="h-6 w-6" />
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-theme-primary mb-1">Office</h3>
                                        <p className="text-theme-muted">
                                            Future Founder Education Pvt. Ltd.<br />
                                            123, Medical Hub, Sector 18<br />
                                            New Delhi - 110001, India
                                        </p>
                                    </div>
                                </div>
                            </GlassmorphicCard>

                            <GlassmorphicCard variant="elevated" className="p-8 bg-gradient-to-br from-primary-blue/10 to-accent-gold/10">
                                <h3 className="text-xl font-display font-semibold text-theme-primary mb-4">
                                    Office Hours
                                </h3>
                                <div className="space-y-2 text-theme-secondary">
                                    <div className="flex justify-between">
                                        <span>Monday - Friday</span>
                                        <span className="font-semibold">9:00 AM - 8:00 PM</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span>Saturday</span>
                                        <span className="font-semibold">10:00 AM - 6:00 PM</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span>Sunday</span>
                                        <span className="font-semibold text-slate-500">Closed</span>
                                    </div>
                                </div>
                            </GlassmorphicCard>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Map Section (Placeholder) */}
            <section className="py-24">
                <div className="container mx-auto px-4">
                    <GlassmorphicCard className="overflow-hidden">
                        <div className="h-96 bg-gradient-to-br from-slate-200 to-slate-300 dark:from-slate-800 dark:to-slate-900 flex items-center justify-center">
                            <div className="text-center">
                                <MapPin className="h-16 w-16 text-primary-blue mx-auto mb-4" />
                                <p className="text-theme-muted">Interactive map coming soon</p>
                            </div>
                        </div>
                    </GlassmorphicCard>
                </div>
            </section>
        </div>
    )
}
